import type { SearchArticleMeta } from "@serophin/nottldr-shared";
import { type UseArticleListResult, useReadArticles } from "@serophin/nottldr-shared/hooks";
import type { ComponentChildren } from "preact";
import { useMemo, useState } from "preact/hooks";
import { ArticleCard } from "../article/ArticleCard";
import { useAudioPlaylist } from "../article/useAudioPlaylist";
import { EmptyList, ListFooter, Spinner, UnreadFilter } from "./ArticleListParts";

export type ArticleListProps = {
  list: UseArticleListResult;
  emptyMessage?: ComponentChildren;
  /** Shown instead of "No more articles" when the list ends. */
  endMessage?: ComponentChildren;
  showReadFilter?: boolean;
};

const useVisibleMetas = (metas: SearchArticleMeta[], unreadOnly: boolean): SearchArticleMeta[] => {
  const { isRead } = useReadArticles();
  return useMemo(() => (unreadOnly ? metas.filter((meta) => !isRead(meta.authorAlias, meta.id)) : metas), [metas, unreadOnly, isRead]);
};

export const ArticleList = ({ list, emptyMessage, endMessage, showReadFilter }: ArticleListProps) => {
  const { metas, authors, isLoading, isLoadingMore, hasMore, loadMore } = list;
  const [unreadOnly, setUnreadOnly] = useState(false);
  const visible = useVisibleMetas(metas, showReadFilter === true && unreadOnly);
  const busy = isLoading || isLoadingMore;
  useAudioPlaylist(visible);

  return (
    <div>
      {showReadFilter && metas.length > 0 && <UnreadFilter unreadOnly={unreadOnly} onChange={setUnreadOnly} />}
      {metas.length === 0 && !busy && <EmptyList message={emptyMessage} />}
      <div class="flex flex-col gap-6">
        {visible.map((meta, index) => (
          <ArticleCard key={`${meta.authorAlias}.${meta.id}`} meta={meta} author={authors[meta.authorAlias]} priority={index < 2} />
        ))}
      </div>
      {busy && <Spinner />}
      {!busy && <ListFooter total={metas.length} shown={visible.length} hasMore={hasMore} endMessage={endMessage} onLoadMore={loadMore} />}
    </div>
  );
};
